/**
 * Low-level GA4 helper (gtag.js script — no Firebase SDK required).
 *
 * WARNING: Never pass PII/PHI into trackEvent() or trackPageView().
 * WARNING: Do not send names, emails, phone numbers, DOB, insurance details,
 * appointment times, tokens, or full URLs with query parameters.
 */

const GA_MEASUREMENT_ID = (
  process.env.REACT_APP_GA_MEASUREMENT_ID ||
  process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
  "G-61TK8X9JDD"
).trim();

const ALLOWED_PARAM_KEYS = new Set([
  "product",
  "platform",
  "step_name",
  "step_number",
  "from_step",
  "payment_type",
  "method",
  "result",
  "provider_id",
  "flow_name",
  "status",
  "error_type",
  "booking_type",
]);

const ALLOWED_PRODUCTS = new Set(["mental_health"]);
const ALLOWED_PLATFORMS = new Set(["web"]);
const ALLOWED_STEP_NAMES = new Set([
  "account",
  "profile",
  "insurance",
  "payment",
  "card_on_file",
  "provider",
  "booking",
  "completed",
]);
const ALLOWED_PAYMENT_TYPES = new Set(["insurance", "self_pay"]);
const ALLOWED_METHODS = new Set(["auto", "manual"]);
const ALLOWED_RESULTS = new Set(["verified", "fallback", "failed"]);

const PROVIDER_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

const THERAPIST_ID_PATH_PATTERN =
  /\/register\/account\/(?:therapists|no-preference\/therapists)\/[^/]+/g;

let isInitialized = false;

const ensureDataLayer = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

const isScriptAlreadyLoaded = (script) => {
  if (!script) {
    return false;
  }
  return (
    script.getAttribute("data-ga-loaded") === "true" ||
    script.readyState === "complete" ||
    script.readyState === "loaded"
  );
};

const loadGtagScript = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve();
  }

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  const existingScript = document.querySelector(
    `script[src="${scriptSrc}"], script[src*="googletagmanager.com/gtag/js"]`
  );

  if (isScriptAlreadyLoaded(existingScript)) {
    return Promise.resolve();
  }

  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener(
        "load",
        () => {
          existingScript.setAttribute("data-ga-loaded", "true");
          resolve();
        },
        { once: true }
      );
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load gtag script")),
        { once: true }
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = scriptSrc;
    script.onload = () => {
      script.setAttribute("data-ga-loaded", "true");
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load gtag script"));
    document.head.appendChild(script);
  });
};

const sanitizeProviderId = (value) => {
  if (value == null || value === "") {
    return undefined;
  }
  const normalized = String(value).trim();
  if (!PROVIDER_ID_PATTERN.test(normalized)) {
    return undefined;
  }
  return normalized;
};

const sanitizeParamValue = (key, value) => {
  if (value == null || value === "") {
    return undefined;
  }

  switch (key) {
    case "product":
      return ALLOWED_PRODUCTS.has(value) ? value : undefined;
    case "platform":
      return ALLOWED_PLATFORMS.has(value) ? value : undefined;
    case "step_name":
    case "from_step":
      return ALLOWED_STEP_NAMES.has(value) ? value : undefined;
    case "step_number": {
      const num = Number(value);
      if (!Number.isFinite(num) || num < 1) {
        return undefined;
      }
      return num;
    }
    case "payment_type":
      return ALLOWED_PAYMENT_TYPES.has(value) ? value : undefined;
    case "method":
      return ALLOWED_METHODS.has(value) ? value : undefined;
    case "result":
      return ALLOWED_RESULTS.has(value) ? value : undefined;
    case "provider_id":
      return sanitizeProviderId(value);
    case "flow_name":
    case "status":
    case "error_type":
    case "booking_type":
      return typeof value === "string" && value.length <= 64 ? value : undefined;
    default:
      return undefined;
  }
};

const sanitizeEventParams = (params = {}) => {
  const sanitized = {};

  Object.entries(params).forEach(([key, value]) => {
    if (!ALLOWED_PARAM_KEYS.has(key)) {
      return;
    }
    const cleanValue = sanitizeParamValue(key, value);
    if (cleanValue !== undefined) {
      sanitized[key] = cleanValue;
    }
  });

  return sanitized;
};

const canTrack = () =>
  Boolean(GA_MEASUREMENT_ID && typeof window !== "undefined" && window.gtag);

/** Strip query/hash and redact dynamic therapist IDs in registration paths. */
export const sanitizePathname = (pathname = "") => {
  let clean = String(pathname).split("?")[0].split("#")[0];
  clean = clean.replace(THERAPIST_ID_PATH_PATTERN, (match) => {
    if (match.includes("/no-preference/therapists/")) {
      return "/register/account/no-preference/therapists/:providerId";
    }
    return "/register/account/therapists/:providerId";
  });
  return clean || "/";
};

export const initAnalytics = () => {
  if (isInitialized || !GA_MEASUREMENT_ID || typeof window === "undefined") {
    return;
  }

  isInitialized = true;
  ensureDataLayer();

  // Queue config immediately so events are not dropped before the script loads.
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  loadGtagScript().catch(() => {
    // Keep the dataLayer stub active even if the external script is blocked.
  });
};

export const trackPageView = (pathname) => {
  if (!canTrack()) {
    return;
  }

  const pagePath = sanitizePathname(pathname);
  window.gtag("event", "page_view", {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (!canTrack() || !eventName) {
    return;
  }

  const sanitizedParams = sanitizeEventParams(params);
  window.gtag("event", eventName, sanitizedParams);
};

export const isAnalyticsEnabled = () => Boolean(GA_MEASUREMENT_ID);
